import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdminCustomer, useUpdateCustomer, useAdminSites } from '@/hooks/useAdmin';
import PageHeader from '@/components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Building2, MapPin, Pencil, PencilOff, User,
} from 'lucide-react';

export default function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: customer, isLoading } = useAdminCustomer(id!);
  const updateCustomer = useUpdateCustomer();
  const { data: rawSites = [] } = useAdminSites();
  const sites = Array.isArray(rawSites) ? rawSites : [];

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: '',
    company: '',
    site_id: '',
  });

  useEffect(() => {
    if (customer) {
      setForm({
        name: customer.name ?? '',
        company: customer.company ?? '',
        site_id: (customer as any).site_id ?? '',
      });
    }
  }, [customer]);

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestForm = useRef(form);
  latestForm.current = form;

  const scheduleAutosave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      if (!id) return;
      const data = latestForm.current;
      updateCustomer.mutate(
        { id, data: { ...data, site_id: data.site_id || undefined } as any },
        { onSuccess: () => toast.success('Changes saved'), onError: () => toast.error('Failed to save') }
      );
    }, 800);
  }, [id, updateCustomer]);

  useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current); }, []);

  const handleChange = (field: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    scheduleAutosave();
  };

  if (isLoading || !customer) {
    return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  }

  const siteName = sites.find((s) => s.id === form.site_id)?.name;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => navigate('/crm/customers')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <PageHeader
          title={customer.name || 'Customer'}
          subtitle={customer.company ? `Company: ${customer.company}` : 'Customer Details'}
        />
      </div>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          {editing ? <Pencil className="h-4 w-4 text-primary" /> : <PencilOff className="h-4 w-4 text-muted-foreground" />}
          <Switch checked={editing} onCheckedChange={setEditing} id="edit-toggle" />
          <Label htmlFor="edit-toggle" className="cursor-pointer text-sm text-muted-foreground">
            {editing ? 'Editing — changes autosave' : 'View mode'}
          </Label>
        </div>
        <Badge variant={customer.is_active ? 'default' : 'outline'}>
          {customer.is_active ? 'Active' : 'Inactive'}
        </Badge>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Customer Information</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-5 sm:grid-cols-2">
          <Field icon={User} label="Customer Name" value={form.name} field="name" editing={editing} onChange={handleChange} />
          <Field icon={Building2} label="Company" value={form.company} field="company" editing={editing} onChange={handleChange} />

          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <MapPin className="h-3.5 w-3.5" /> Site
            </Label>
            {editing ? (
              <Select value={form.site_id} onValueChange={(v) => handleChange('site_id', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a site…" />
                </SelectTrigger>
                <SelectContent>
                  {sites.filter((s) => s.is_active).map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm font-medium text-foreground">{siteName || customer.site_name || '—'}</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({
  icon: Icon,
  label,
  value,
  field,
  editing,
  onChange,
  type = 'text',
}: {
  icon: typeof User;
  label: string;
  value: string;
  field: string;
  editing: boolean;
  onChange: (field: any, value: string) => void;
  type?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </Label>
      {editing ? (
        <Input type={type} value={value} onChange={(e) => onChange(field, e.target.value)} />
      ) : (
        <p className="text-sm font-medium text-foreground">{value || '—'}</p>
      )}
    </div>
  );
}
